import React, { useEffect, useLayoutEffect } from 'react';
import { Sun, Moon, LogOut, Star, User, Plug } from 'lucide-react';
import { Navigate, Route, Routes, useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { FeatureAccessProvider } from './contexts/FeatureAccessContext';
import { TabStateProvider } from './contexts/TabStateContext';
import { TabActivityProvider } from './contexts/TabActivityContext';
import { SearchWorkspaceProvider } from './contexts/SearchWorkspaceContext';
import { AgenticSearchWorkspaceProvider } from './contexts/AgenticSearchWorkspaceContext';
import { AutomationWorkspaceProvider } from './contexts/AutomationWorkspaceContext';
import {
  apiService,
  setEmailVerificationRequiredHandler,
  setTokenFunction,
  UserProfile,
  BillingAccount,
} from './services/api';
import { retrySafeRead } from './services/safeRead';
import { useProcessingActivity } from './hooks/useProcessingActivity';
import Sidebar from './components/Sidebar';
import SearchTab from './components/SearchTab';
import IndexTab from './components/IndexTab';
import WorkflowsTab from './components/WorkflowsTab';
import RealtimeTab from './components/RealtimeTab';
import PlaygroundTab from './components/PlaygroundTab';
import MarkedTab from './components/MarkedTab';
import CloudConnectorsTab from './components/CloudConnectorsTab';
import WebhooksTab from './components/WebhooksTab';
import ApiKeysTab from './components/ApiKeysTab';
import UsageTab from './components/UsageTab';
import JobsTab from './components/JobsTab';
import UploadQueueModal, { UploadQueueLauncher } from './components/UploadQueueModal';
import ProcessingConnectionNotice from './components/ProcessingConnectionNotice';
import BillingTab from './components/BillingTab';
import McpTab from './components/McpTab';
import McpConnectionDialog from './components/McpConnectionDialog';
import ProfileTab from './components/ProfileTab';
import LoginPage from './components/LoginPage';
import LegalPage, { LegalTopic } from './components/LegalPage';
import CookieBanner from './components/CookieBanner';
import ToltConsentLoader from './components/ToltConsentLoader';
import EmailVerificationActionPage from './components/EmailVerificationActionPage';
import EmailVerificationGatePage from './components/EmailVerificationGatePage';
import WorkOSOAuthCompletionPage from './components/WorkOSOAuthCompletionPage';
import AuthFlowShell, { AuthLoadingCard } from './components/AuthFlowShell';
import LockedFeaturePanel from './components/LockedFeaturePanel';
import FeatureAction from './components/FeatureAction';
import { getLockedTabs, getUpgradeLabel, STARTER_PLAN_PATH } from './navigation/featureAccess';
import { getCheckoutReturnDestination, parseCheckoutReturnParams } from './navigation/checkoutReturn';
import { safeLocalStorageRemove } from './utils/safeStorage';
import {
  bindPendingWorkOSOAuthToUser,
  captureWorkOSOAuthFromSearch,
  getWorkOSOAuthSessionStorage,
  isWorkOSOAuthLoginPath,
} from './navigation/workosOAuth';
import { isResultDestination } from './navigation/resultLinks';
import { DEFAULT_TAB, getTabFromPathname, getTabPath, TabType } from './navigation/tabRoutes';
import { shouldRequireEmailVerification } from './utils/authGuards';
import { parseFirebaseAuthAction } from './utils/firebaseAuthActions';
import {
  STORAGE_CONSENT_EVENT,
  StorageConsentEventDetail,
} from './privacy/storageConsent';
import { registerVerifiedAffiliateLead } from './services/toltAffiliate';
import {
  BRAND_FAVICON_DARK_SRC,
  BRAND_FAVICON_LIGHT_SRC,
  BRAND_LOGO_DARK_SRC,
  BRAND_LOGO_LIGHT_SRC,
  BRAND_MARK_SRC,
  PRODUCT_NAME,
} from './brand';
import './App.css';

const WORKSPACE_TABS: TabType[] = [
  'playground', 'search', 'index', 'workflows', 'realtime', 'marked',
  'connectors', 'webhooks', 'apikeys', 'usage', 'jobs', 'billing', 'mcp', 'profile',
];

function getPublicLegalTopic(pathname: string): LegalTopic | null {
  const normalizedPath = pathname.replace(/\/+$/, '') || '/';

  switch (normalizedPath) {
    case '/terms':
      return 'terms';
    case '/privacy':
      return 'privacy';
    case '/cookies':
      return 'cookies';
    case '/accessibility':
      return 'accessibility';
    default:
      return null;
  }
}

function getInitialDarkMode(): boolean {
  const savedTheme = getSavedThemePreference();
  const prefersDark = getColorSchemeMediaQuery()?.matches ?? false;
  return savedTheme === 'dark' || (!savedTheme && prefersDark);
}

function getSavedThemePreference(): 'dark' | 'light' | null {
  try {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme === 'dark' || savedTheme === 'light' ? savedTheme : null;
  } catch (error) {
    console.warn('Unable to read theme preference:', error);
    return null;
  }
}

function saveThemePreference(isDarkMode: boolean) {
  try {
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
  } catch (error) {
    console.warn('Unable to save theme preference:', error);
  }
}

function getColorSchemeMediaQuery(): MediaQueryList | null {
  return typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : null;
}

function applyTheme(isDarkMode: boolean) {
  document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
}

function updateFavicon(src: string) {
  let iconLink = document.querySelector<HTMLLinkElement>('link[rel~="icon"]');
  if (!iconLink) {
    iconLink = document.createElement('link');
    iconLink.rel = 'icon';
    document.head.appendChild(iconLink);
  }

  iconLink.type = 'image/png';
  iconLink.href = src;
}

const getLegacyEnginesLocation = (search: string): string | null => {
  const legacyParams = new URLSearchParams(search);
  const promptId = legacyParams.get('prompt')?.trim() || '';
  const shouldCreate = legacyParams.get('promptNew') === '1';
  const isLegacyPromptView = legacyParams.get('view') === 'prompt';

  if (!isLegacyPromptView && !promptId && !shouldCreate) {
    return null;
  }

  const enginesParams = new URLSearchParams();
  if (promptId) {
    enginesParams.set('prompt', promptId);
    enginesParams.set('edit', '1');
  }
  if (shouldCreate) {
    enginesParams.set('new', '1');
  }

  const promptSearch = legacyParams.get('pq')?.trim() || '';
  if (promptSearch) {
    enginesParams.set('q', promptSearch);
  }

  const enginesSearch = enginesParams.toString();
  return `${getTabPath('workflows')}${enginesSearch ? `?${enginesSearch}` : ''}`;
};

const LegacyEnginesRoute: React.FC = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  if (params.has('prompt') && params.get('new') !== '1') params.set('edit', '1');
  return <Navigate to={`/workflows${params.size ? `?${params}` : ''}`} replace />;
};

const LegacyAutomationsRoute: React.FC = () => {
  const location = useLocation();
  const previous = new URLSearchParams(location.search);
  const params = new URLSearchParams({ automation: previous.get('view') === 'export' ? 'export' : 'import' });
  if (previous.get('index')) params.set('index', previous.get('index')!);
  return <Navigate to={`/index?${params}`} replace />;
};

function AppContent() {
  const [isMcpConnectionOpen, setIsMcpConnectionOpen] = React.useState(false);
  const [isDarkMode, setIsDarkMode] = React.useState(getInitialDarkMode);
  const [hasThemePreference, setHasThemePreference] = React.useState(
    () => getSavedThemePreference() !== null
  );
  const { currentUser, logout, getIdToken, signInProvider, refreshVerificationStatus, interactiveLoginEvent, acknowledgeInteractiveLogin } = useAuth();
  const [profile, setProfile] = React.useState<UserProfile | null>(null);
  const [billingAccount, setBillingAccount] = React.useState<BillingAccount | null>(null);
  const [profileError, setProfileError] = React.useState<string | null>(null);
  const [backendRequiresEmailVerification, setBackendRequiresEmailVerification] = React.useState(false);
  const [affiliateConsentRevision, setAffiliateConsentRevision] = React.useState(0);
  const affiliateBootstrapCompletedUserIdsRef = React.useRef(new Set<string>());
  const snapshotReadRef = React.useRef<Promise<void> | null>(null);
  const billingReadRef = React.useRef<Promise<BillingAccount> | null>(null);
  const accountLoadGenerationRef = React.useRef(0);
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const isWorkOSOAuthLogin = isWorkOSOAuthLoginPath(location.pathname);
  const checkoutDestination = getCheckoutReturnDestination(location.pathname, location.search);
  const activeTab = getTabFromPathname(location.pathname) ?? DEFAULT_TAB;
  const legacyPlaygroundLocation = activeTab === 'playground'
    ? getLegacyEnginesLocation(location.search) : null;
  const isPlaygroundActive = activeTab === 'playground' && !legacyPlaygroundLocation;
  // Retain each visited workspace for this auth-scoped lifetime. Freeze its
  // router input while hidden so another tab cannot hydrate its URL consumers.
  const tabLocationsRef = React.useRef<Partial<Record<TabType, {
    location: typeof location;
    navigationType: typeof navigationType;
  }>>>({});
  if (getTabFromPathname(location.pathname) && WORKSPACE_TABS.includes(activeTab) && !legacyPlaygroundLocation && !checkoutDestination) {
    tabLocationsRef.current[activeTab] = { location, navigationType };
  }
  const shouldShowAuthAction = parseFirebaseAuthAction(location.search, location.pathname).active;
  const currentUserId = currentUser?.uid || null;
  const currentUserEmailVerified = Boolean(currentUser?.emailVerified);
  const requiresEmailVerification =
    shouldRequireEmailVerification(currentUser, signInProvider) ||
    Boolean(currentUser && backendRequiresEmailVerification);
  const lockedTabs = React.useMemo(() => getLockedTabs(billingAccount), [billingAccount]);
  useEffect(() => {
    if (lockedTabs.mcp) setIsMcpConnectionOpen(false);
  }, [lockedTabs.mcp]);
  const isLocalhost = ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
  const isE2EMockAuth = process.env.REACT_APP_E2E_MOCK_AUTH === 'true' && isLocalhost;
  const e2eParams = new URLSearchParams(location.search);
  const e2eView = e2eParams.get('e2e');
  const requestedE2EPlan = e2eParams.get('e2ePlan');
  const e2ePlanId = requestedE2EPlan === 'starter' || requestedE2EPlan === 'pro'
    ? requestedE2EPlan
    : 'free';
  const allowE2EAppShell = isE2EMockAuth && e2eView === 'app';

  const e2eMockProfile: UserProfile | null = React.useMemo(() => {
    if (!isE2EMockAuth || !currentUser?.uid) return null;
    return {
      user_id: currentUser.uid,
      email: currentUser.email || 'e2e.user@example.com',
      email_verified: true,
      display_name: 'E2E User',
      company_name: 'E2E Labs',
      job_title: null,
      phone: null,
      timezone: null,
      avatar_url: null,
      created_at: null,
      updated_at: null,
      last_login_at: null,
      plan_selection_required: false,
      auth: null,
      onboarding: { completed: true, completed_at: null, steps_completed: [], skipped: false, flow_version: 2 },
    };
  }, [currentUser?.email, currentUser?.uid, isE2EMockAuth]);

  const e2eMockBillingAccount: BillingAccount | null = React.useMemo(() => {
    if (!isE2EMockAuth || !currentUser?.uid) return null;
    return {
      user_id: currentUser.uid,
      stripe_customer_id: null,
      plan_id: e2ePlanId,
      trial_eligible: e2ePlanId === 'free',
      trial_started_at: null,
      trial_ends_at: null,
      balance_mt: e2ePlanId === 'free' ? 50 : 500,
      subscription_cycle_remaining_mt: 0,
      stripe_subscription_id: null,
      stripe_subscription_status: null,
      pending_plan_id: null,
      pending_plan_effective_at: null,
      pending_plan_status: null,
      pending_plan_change_type: null,
      paid_through: null,
      last_payment_failure_message: null,
      lifetime_credits_purchased: 100,
      lifetime_credits_used: 0,
      auto_recharge_enabled: false,
      auto_recharge_threshold_mt: null,
      auto_recharge_amount_mt: null,
      created_at: null,
      updated_at: null,
    };
  }, [currentUser?.uid, e2ePlanId, isE2EMockAuth]);

  const refreshAccount = React.useCallback(async (): Promise<BillingAccount> => {
    if (billingReadRef.current) return billingReadRef.current;
    if (isE2EMockAuth && e2eMockBillingAccount) {
      setBillingAccount(e2eMockBillingAccount);
      return e2eMockBillingAccount;
    }
    if (!currentUserId || requiresEmailVerification) throw new Error('A verified account is required');
    const generation = accountLoadGenerationRef.current;
    const includeAffiliateReferral = !affiliateBootstrapCompletedUserIdsRef.current.has(currentUserId);
    const request = retrySafeRead(() => apiService.getBillingAccount({ includeAffiliateReferral }));
    billingReadRef.current = request;
    try {
      const account = await request;
      if (generation === accountLoadGenerationRef.current) setBillingAccount(account);
      return account;
    } finally {
      if (billingReadRef.current === request) billingReadRef.current = null;
    }
  }, [currentUserId, e2eMockBillingAccount, isE2EMockAuth, requiresEmailVerification]);

  const refreshProfile = React.useCallback(async (): Promise<void> => {
    if (snapshotReadRef.current) return snapshotReadRef.current;
    if (isE2EMockAuth) {
      if (e2eMockProfile) {
        setProfile(e2eMockProfile);
        setBillingAccount(e2eMockBillingAccount);
        setProfileError(null);
      }
      return;
    }
    if (!currentUser?.uid || requiresEmailVerification) {
      return;
    }
    const generation = accountLoadGenerationRef.current;
    const accountUserId = currentUser.uid;
    const includeAffiliateReferral = !affiliateBootstrapCompletedUserIdsRef.current.has(accountUserId);
    const request = (async () => {
      const [p, account] = await Promise.all([
        retrySafeRead(() => apiService.getUserProfile({ includeAffiliateReferral })),
        refreshAccount(),
      ]);
      if (generation !== accountLoadGenerationRef.current) return;
      affiliateBootstrapCompletedUserIdsRef.current.add(accountUserId);
      setProfile(p);
      setBillingAccount(account);
      setProfileError(null);
    })();
    snapshotReadRef.current = request;
    try {
      await request;
    } finally {
      if (snapshotReadRef.current === request) snapshotReadRef.current = null;
    }
  }, [currentUser?.uid, e2eMockBillingAccount, e2eMockProfile, isE2EMockAuth, refreshAccount, requiresEmailVerification]);

  const rendersAuthenticatedAppShell = Boolean(currentUser || allowE2EAppShell) &&
    !requiresEmailVerification &&
    Boolean(profile && billingAccount) &&
    !profileError;
  const processingPageEntryReady = !shouldShowAuthAction && !isWorkOSOAuthLogin
    && !checkoutDestination && !legacyPlaygroundLocation;

  useProcessingActivity({
    userId: !requiresEmailVerification ? currentUserId : null,
    advisoryEnabled: !isE2EMockAuth,
    pageEntryReady: processingPageEntryReady,
    workspaceReady: rendersAuthenticatedAppShell && processingPageEntryReady,
    pathname: location.pathname,
    search: location.search,
    interactiveLoginEvent,
    onInteractiveLoginConsumed: acknowledgeInteractiveLogin,
    mcpEnabled: !lockedTabs.mcp,
  });

  useEffect(() => {
    applyTheme(isDarkMode);
  }, [isDarkMode]);

  useEffect(() => {
    updateFavicon(rendersAuthenticatedAppShell && !isDarkMode
      ? BRAND_FAVICON_LIGHT_SRC
      : BRAND_FAVICON_DARK_SRC
    );
  }, [isDarkMode, rendersAuthenticatedAppShell]);

  useEffect(() => {
    if (hasThemePreference) {
      return;
    }

    const mediaQuery = getColorSchemeMediaQuery();
    if (!mediaQuery) {
      return;
    }

    const handleSystemThemeChange = (event: MediaQueryListEvent) => {
      setIsDarkMode(event.matches);
    };

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleSystemThemeChange);
      return () => mediaQuery.removeEventListener('change', handleSystemThemeChange);
    }

    mediaQuery.addListener(handleSystemThemeChange);
    return () => mediaQuery.removeListener(handleSystemThemeChange);
  }, [hasThemePreference]);

  useLayoutEffect(() => {
    // Set up the token function for API calls
    setTokenFunction(getIdToken);
    return () => setTokenFunction(null);
  }, [getIdToken]);

  useLayoutEffect(() => {
    if (!isWorkOSOAuthLogin || !currentUserId) return;
    bindPendingWorkOSOAuthToUser(currentUserId, getWorkOSOAuthSessionStorage());
  }, [currentUserId, isWorkOSOAuthLogin, location.key]);

  const handleEmailVerificationRequired = React.useCallback(async () => {
    setProfile(null);
    setBillingAccount(null);
    setProfileError(null);
    setBackendRequiresEmailVerification(true);

    if (!currentUser) {
      return;
    }

    try {
      setBackendRequiresEmailVerification(!(await refreshVerificationStatus()));
    } catch (error) {
      console.warn('Failed to refresh email verification status after scoped API rejection:', error);
    }
  }, [currentUser, refreshVerificationStatus]);

  useLayoutEffect(() => {
    setEmailVerificationRequiredHandler(handleEmailVerificationRequired);
    return () => setEmailVerificationRequiredHandler(null);
  }, [handleEmailVerificationRequired]);

  useEffect(() => {
    if (!currentUserId || currentUserEmailVerified) {
      setBackendRequiresEmailVerification(false);
    }
  }, [currentUserId, currentUserEmailVerified]);

  useEffect(() => {
    const handleConsentUpdate = (event: Event) => {
      const detail = (event as CustomEvent<StorageConsentEventDetail>).detail;
      if (detail?.consent.v === 2) {
        setAffiliateConsentRevision((revision) => revision + 1);
      }
    };

    window.addEventListener(STORAGE_CONSENT_EVENT, handleConsentUpdate);
    return () => window.removeEventListener(STORAGE_CONSENT_EVENT, handleConsentUpdate);
  }, []);

  useEffect(() => {
    accountLoadGenerationRef.current += 1;
    snapshotReadRef.current = null;
    billingReadRef.current = null;
    if (!currentUserId || requiresEmailVerification) {
      setProfile(null);
      setBillingAccount(null);
      setProfileError(null);
      return;
    }

    let cancelled = false;
    setProfileError(null);
    void refreshProfile().catch((err) => {
      console.error('Failed to load user profile:', err);
      if (!cancelled) setProfileError('Failed to load your account. Please retry.');
    });

    return () => {
      cancelled = true;
      accountLoadGenerationRef.current += 1;
    };
  }, [currentUserId, refreshProfile, requiresEmailVerification]);

  useEffect(() => {
    if (
      isE2EMockAuth
      || !currentUser?.uid
      || requiresEmailVerification
      || !profile?.email_verified
    ) {
      return;
    }

    // Tolt is deliberately outside the account bootstrap's critical path.
    // Failed vendor calls remain retryable when a later billing-account load
    // produces a new object or when consent changes.
    void registerVerifiedAffiliateLead(billingAccount?.affiliate_lead_identifier);
  }, [
    affiliateConsentRevision,
    currentUser?.uid,
    billingAccount,
    isE2EMockAuth,
    profile,
    requiresEmailVerification,
  ]);

  const toggleTheme = () => {
    const newTheme = !isDarkMode;
    setIsDarkMode(newTheme);
    setHasThemePreference(true);
    saveThemePreference(newTheme);
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  if (isE2EMockAuth && e2eView === 'verify') {
    return <EmailVerificationGatePage mockEmail="e2e.user@example.com" disableActions />;
  }

  if (shouldShowAuthAction) {
    return (
      <>
        <EmailVerificationActionPage />
        <CookieBanner />
      </>
    );
  }

  if (!allowE2EAppShell) {
    let authFlowContent: React.ReactNode | null = null;
    let authFlowBusy = false;

    if (!currentUser) {
      authFlowContent = <LoginPage embedded />;
    } else if (requiresEmailVerification) {
      return (
        <>
          <EmailVerificationGatePage />
          <CookieBanner />
        </>
      );
    } else if (!profileError && (!profile || !billingAccount)) {
      authFlowBusy = true;
      authFlowContent = <AuthLoadingCard />;
    } else if (profileError) {
      authFlowContent = (
          <div className="auth-flow-card auth-flow-card-wide auth-flow-loading-card">
            <div className="auth-flow-brand" aria-label={PRODUCT_NAME}>
              <img className="auth-flow-brand-mark" src={BRAND_MARK_SRC} alt="" aria-hidden="true" />
              <span>{PRODUCT_NAME}</span>
            </div>
            <p className="auth-flow-eyebrow">Account unavailable</p>
            <h1 className="auth-flow-title">We couldn't load your account</h1>
            <p className="auth-flow-copy">{profileError}</p>
            <div className="auth-flow-actions">
              <button
                className="auth-flow-primary"
                onClick={() => {
                  setProfileError(null);
                  void refreshProfile().catch(() => setProfileError('Failed to load your account. Please retry.'));
                }}
              >
                Retry
              </button>
              <button className="auth-flow-secondary" onClick={handleLogout}>
                Logout
              </button>
            </div>
          </div>
      );
    }

    if (authFlowContent) {
      return (
        <>
          <AuthFlowShell busy={authFlowBusy}>{authFlowContent}</AuthFlowShell>
          <CookieBanner />
        </>
      );
    }
  }

  if (isWorkOSOAuthLogin && currentUser && profile && billingAccount) {
    return <WorkOSOAuthCompletionPage />;
  }

  // Checkout links created before billing moved into the app can return to
  // signup or workspace URLs. Preserve the full return context for Billing.
  if (checkoutDestination) return <Navigate to={checkoutDestination} replace />;

  const userEmail = currentUser?.email || (allowE2EAppShell ? 'e2e.user@example.com' : '');
  const appBrandLogoSrc = isDarkMode ? BRAND_LOGO_DARK_SRC : BRAND_LOGO_LIGHT_SRC;

  const handleTabChange = (tab: TabType) => {
    const targetPath = getTabPath(tab);
    if (location.pathname !== targetPath) {
      navigate({
        pathname: targetPath,
        search: tabLocationsRef.current[tab]?.location.search || '',
      });
    }
  };

  const renderTabElement = (tab: TabType, element: React.ReactElement) => {
    const lockedFeature = lockedTabs[tab];
    return lockedFeature ? <LockedFeaturePanel feature={lockedFeature} /> : element;
  };
  const automationWorkspaceEnabled = Boolean(currentUserId) && !lockedTabs.automations;
  const tabElements: Partial<Record<TabType, React.ReactElement | null>> = {
    playground: <PlaygroundTab isActive={isPlaygroundActive} />,
    search: <SearchTab key={billingAccount?.plan_id === 'free' ? 'demo' : 'subscribed'} />,
    index: <IndexTab key={billingAccount?.plan_id === 'free' ? 'demo' : 'subscribed'} />,
    workflows: <WorkflowsTab isActive={activeTab === 'workflows'} />,
    realtime: <RealtimeTab />,
    marked: <MarkedTab />,
    connectors: <CloudConnectorsTab />,
    webhooks: <WebhooksTab />,
    apikeys: <ApiKeysTab />,
    usage: <UsageTab />,
    jobs: <JobsTab />,
    billing: billingAccount ? <BillingTab account={billingAccount} refreshAccount={refreshAccount} /> : null,
    mcp: <McpTab />,
    profile: <ProfileTab />,
  };

  return (
    <FeatureAccessProvider account={billingAccount}>
    <AutomationWorkspaceProvider
      key={currentUserId ?? '__signed_out__'}
      enabled={automationWorkspaceEnabled}
    >
    <AgenticSearchWorkspaceProvider isActive={isPlaygroundActive}>
    <div className="app">
      <header className="app-header">
        <div className="app-header-content">
          <h1 className="app-title" aria-label={PRODUCT_NAME}>
            <img className="app-brand-logo" src={appBrandLogoSrc} alt="" aria-hidden="true" />
            <span className="app-product-name">{PRODUCT_NAME}</span>
          </h1>
          <div className="app-header-actions">
            <UploadQueueLauncher />
            {billingAccount?.plan_id === 'free' && <button
              type="button" className="app-header-mcp-button"
              onClick={() => navigate(STARTER_PLAN_PATH)}
            >{getUpgradeLabel(billingAccount)}</button>}
            <div className="app-header-user-info">
              <User size={16} />
              <span className="app-header-user-email">{userEmail}</span>
            </div>
            <FeatureAction feature="mcp_integrations"><button
              type="button"
              className="app-header-mcp-button"
              onClick={() => {
                if (lockedTabs.mcp) {
                  handleTabChange('mcp');
                } else {
                  setIsMcpConnectionOpen(true);
                }
              }}
              aria-haspopup={lockedTabs.mcp ? undefined : 'dialog'}
            >
              <Plug size={16} aria-hidden="true" />
              Connect MCP
            </button></FeatureAction>
            <FeatureAction feature="workspace_mutation"><button
              type="button"
              className={`app-header-icon-button app-header-marked-button ${activeTab === 'marked' ? 'active' : ''}`}
              onClick={() => handleTabChange('marked')}
              title="Marked"
              aria-label="Marked"
              aria-current={activeTab === 'marked' ? 'page' : undefined}
            >
              <Star size={20} aria-hidden="true" />
            </button></FeatureAction>
            <button
              type="button"
              className="app-header-icon-button app-header-theme-toggle"
              onClick={toggleTheme}
              aria-label="Toggle theme"
            >
              {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
            </button>
            <button
              type="button"
              className="app-header-logout-button"
              onClick={handleLogout}
              aria-label="Logout"
            >
              <LogOut size={18} />
              <span className="app-header-logout-label">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <div className="app-content">
        <Sidebar activeTab={activeTab} onTabChange={handleTabChange} lockedTabs={lockedTabs} />
        <main className={`main-content ${activeTab === 'index' ? 'index-tab-active' : ''}`}>
          {WORKSPACE_TABS.map((tab) => {
            const saved = tabLocationsRef.current[tab];
            if (!saved) return null;
            const isActive = activeTab === tab && !legacyPlaygroundLocation;
            return <TabActivityProvider key={tab} isActive={isActive} navigationType={saved.navigationType}>
              <div className="retained-workspace" data-workspace={tab} hidden={!isActive}>
                <Routes location={saved.location}>
                  <Route path={getTabPath(tab)} element={tabElements[tab] ? renderTabElement(tab, tabElements[tab]!) : null} />
                </Routes>
              </div>
            </TabActivityProvider>;
          })}
          <Routes>
            <Route path="/" element={<Navigate to={getTabPath(DEFAULT_TAB)} replace />} />
            {WORKSPACE_TABS.filter((tab) => tab !== 'playground').map((tab) => (
              <Route key={tab} path={getTabPath(tab)} element={null} />
            ))}
            <Route path={getTabPath('automations')} element={<LegacyAutomationsRoute />} />
            <Route path={getTabPath('playground')} element={legacyPlaygroundLocation ? <Navigate to={legacyPlaygroundLocation} replace /> : null} />
            <Route path={getTabPath('engines')} element={<LegacyEnginesRoute />} />
            <Route path="*" element={<Navigate to={getTabPath(DEFAULT_TAB)} replace />} />
          </Routes>
        </main>
      </div>
      <CookieBanner />
      <ProcessingConnectionNotice />
      <UploadQueueModal />
      {isMcpConnectionOpen && !lockedTabs.mcp && (
        <McpConnectionDialog onClose={() => setIsMcpConnectionOpen(false)} />
      )}
    </div>
    </AgenticSearchWorkspaceProvider>
    </AutomationWorkspaceProvider>
    </FeatureAccessProvider>
  );
}

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const publicLegalTopic = getPublicLegalTopic(location.pathname);
  const isUnscrubbedWorkOSOAuthLogin = (
    isWorkOSOAuthLoginPath(location.pathname)
    && new URLSearchParams(location.search).has('external_auth_id')
  );
  const handleInteractiveLogin = React.useCallback(() => {
    if (
      isWorkOSOAuthLoginPath(location.pathname)
      || isResultDestination(location.pathname, location.search)
      || parseFirebaseAuthAction(location.search, location.pathname).active
      || location.pathname === '/billing'
      || parseCheckoutReturnParams(location.search).isReturn
    ) return;
    const currentParams = new URLSearchParams(location.search);
    const retainedParams = new URLSearchParams();
    ['ref'].forEach((key) => {
      const value = currentParams.get(key);
      if (value) retainedParams.set(key, value);
    });
    navigate({ pathname: '/', search: retainedParams.toString() }, { replace: true });
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!isWorkOSOAuthLoginPath(location.pathname)) return;
    const capture = captureWorkOSOAuthFromSearch(
      location.search,
      getWorkOSOAuthSessionStorage(),
    );
    if (!capture.hadExternalAuthId) return;
    navigate(
      {
        pathname: location.pathname,
        search: capture.sanitizedSearch,
        hash: location.hash,
      },
      { replace: true, state: location.state },
    );
  }, [location.hash, location.pathname, location.search, location.state, navigate]);

  useEffect(() => {
    // Retire the former cross-session signup plan intent without applying it.
    safeLocalStorageRemove('vectormethods.planIntent');
    const initialDarkMode = getInitialDarkMode();
    applyTheme(initialDarkMode);
    updateFavicon(initialDarkMode ? BRAND_FAVICON_DARK_SRC : BRAND_FAVICON_LIGHT_SRC);
  }, []);

  if (publicLegalTopic) {
    return (
      <>
        <ToltConsentLoader />
        <LegalPage topic={publicLegalTopic} />
        <CookieBanner />
      </>
    );
  }

  // A client-side navigation can bypass the pre-React browser bootstrap. Do
  // not mount authentication or completion effects until its URL is scrubbed.
  if (isUnscrubbedWorkOSOAuthLogin) {
    return null;
  }

  return (
    <>
      <ToltConsentLoader />
      <AuthProvider
        onInteractiveLogin={handleInteractiveLogin}
        loadingFallback={<><AuthFlowShell busy><AuthLoadingCard /></AuthFlowShell><CookieBanner /></>}
      >
        <AuthScopedTabStateProvider>
          <AppContent />
        </AuthScopedTabStateProvider>
      </AuthProvider>
    </>
  );
}

function AuthScopedTabStateProvider({ children }: { children: React.ReactNode }) {
  const { currentUser } = useAuth();
  const authScope = currentUser?.uid ?? null;
  return (
    <TabStateProvider
      key={authScope ?? '__signed_out__'}
      userId={authScope}
    >
      {authScope ? (
        <SearchWorkspaceProvider key={authScope} userId={authScope}>
          {children}
        </SearchWorkspaceProvider>
      ) : (
        <SearchWorkspaceProvider key="__signed_out__" userId={null}>
          {children}
        </SearchWorkspaceProvider>
      )}
    </TabStateProvider>
  );
}

export default App;
